import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Typography from '@mui/material/Typography';

import { theme, Title } from '@bloomays-lib/ui.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';

import { useTalentBoardStore } from '../../stores/talentBoardStore';
import { useBloomies } from '../../hooks/useBloomies';
import Contact from '../atoms/Contact';
import { Logger } from '../../services/service-logger';
import { Role, TalentBoardEventType } from '@bloomays-lib/types.shared';

const logger = Logger('SideBar');

export type SideBarProps = {
  email: string;
  isCDI: boolean;
};

const TabItem = ({
  isExpanded,
  isMobile,
  isActive,
  label,
  icon,
  onClick,
  hideTextOnMobile,
}: {
  isExpanded: boolean;
  isMobile: boolean;
  isActive: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  hideTextOnMobile?: boolean;
}) => {
  return (
    <TabItemContainer
      isExpanded={isExpanded || isMobile}
      isActive={isActive}
      hideTextOnMobile={hideTextOnMobile}
      onClick={onClick}
    >
      {icon}
      {(isExpanded || isMobile) && (
        <TabText isActive={isActive} hideTextOnMobile={hideTextOnMobile}>
          {label}
        </TabText>
      )}
    </TabItemContainer>
  );
};

const SideBar = ({ email, isCDI }: SideBarProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const auth = useAuthStore((state) => state.auth);
  const isClient = auth?.user?.roles?.includes(Role.Client) || false;
  const { navState, setNavState, isExpanded, setIsExpanded, setShareModalOpen, setEventsModalOpen } =
    useTalentBoardStore();
  // Récupération de l'account manager via l'email du job (seulement si job existe)
  const { bloomie: contact, loading: loadingContact } = useBloomies({
    email,
  });

  const navigate = useNavigate();
  const { t } = useTranslation('talentBoard');

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <SideBarContainer isExpanded={isExpanded}>
      <MenuSection>
        <TitleSection isExpanded={isExpanded}>
          {isExpanded && <Title textTitle={t('sideBar.title')} variant="heading8" sx={{ fontSize: '20px' }} />}
          <ToggleButton onClick={toggleSidebar} isExpanded={isExpanded}>
            <KeyboardDoubleArrowLeftOutlinedIcon />
          </ToggleButton>
        </TitleSection>
        <TabsSection>
          <TabItem
            isExpanded={isExpanded}
            isMobile={isMobile}
            isActive={false}
            label={t('sideBar.sideBarLinks.backToJobs')}
            icon={
              <TabIcon isActive={false}>
                <ArrowBackOutlinedIcon />
              </TabIcon>
            }
            hideTextOnMobile
            onClick={() => {
              logger.info('Traffic', { page: 'TalentBoard', evt: { name: TalentBoardEventType.TalentboardListJobs } });
              void navigate('/talentboard/jobs');
            }}
          />
          <TabItem
            isExpanded={isExpanded}
            isMobile={isMobile}
            isActive={navState === 'talentboard'}
            label={t('sideBar.sideBarLinks.talentBoard')}
            icon={
              <TabIcon isActive={navState === 'talentboard'}>
                <GroupsOutlinedIcon />
              </TabIcon>
            }
            onClick={() => {
              logger.info('Traffic', {
                page: 'TalentBoard',
                evt: { name: TalentBoardEventType.TalentboardOpenJobTalentBoard },
              });
              setNavState('talentboard');
            }}
          />
          <TabItem
            isExpanded={isExpanded}
            isMobile={isMobile}
            isActive={navState === 'resume'}
            label={isCDI ? t('sideBar.sideBarLinks.jobResume') : t('sideBar.sideBarLinks.missionResume')}
            icon={
              <TabIcon isActive={navState === 'resume'}>
                <AssignmentOutlinedIcon />
              </TabIcon>
            }
            onClick={() => {
              logger.info('Traffic', {
                page: 'TalentBoard',
                evt: { name: TalentBoardEventType.TalentboardOpenJobDetails },
              });
              setNavState('resume');
            }}
          />
          <TabItem
            isExpanded={isExpanded}
            isMobile={isMobile}
            isActive={false}
            label={t('sideBar.sideBarLinks.shareTalentBoard')}
            icon={
              <TabIcon isActive={false}>
                <ShareOutlinedIcon />
              </TabIcon>
            }
            hideTextOnMobile
            onClick={() => {
              logger.info('Traffic', {
                page: 'TalentBoard',
                evt: { name: TalentBoardEventType.TalentboardOpenShareJob },
              });
              setShareModalOpen(true);
            }}
          />
          {!isMobile && !isClient && (
            <TabItem
              isExpanded={isExpanded}
              isMobile={isMobile}
              isActive={false}
              label={t('sideBar.sideBarLinks.eventsDashboard')}
              icon={
                <TabIcon isActive={false}>
                  <HistoryOutlinedIcon />
                </TabIcon>
              }
              hideTextOnMobile
              onClick={() => {
                logger.info('Traffic', {
                  page: 'TalentBoard',
                  evt: { name: TalentBoardEventType.TalentboardOpenEventsDashboard },
                });
                setEventsModalOpen(true);
              }}
            />
          )}
        </TabsSection>
      </MenuSection>

      {isExpanded && <Contact contact={contact} loading={loadingContact} />}
    </SideBarContainer>
  );
};

const SideBarContainer = styled('div', { shouldForwardProp: (prop) => prop !== 'isExpanded' })<{ isExpanded: boolean }>(
  ({ isExpanded, theme }) => ({
    width: isExpanded ? '285px' : '48px',
    height: 'calc(100vh - 80px)',
    backgroundColor: theme.palette.green[60],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '24px',
    [theme.breakpoints.down('md')]: {
      backgroundColor: theme.palette.neutral[10],
      height: '72px',
      width: '100%',
      padding: '8px',
      justifyContent: 'center',
    },
  }),
);

const MenuSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}));

const TitleSection = styled('div', { shouldForwardProp: (prop) => prop !== 'isExpanded' })<{ isExpanded: boolean }>(
  ({ isExpanded, theme }) => ({
    display: 'flex',
    justifyContent: isExpanded ? 'space-between' : 'center',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  }),
);

const ToggleButton = styled('button', { shouldForwardProp: (prop) => prop !== 'isExpanded' })<{ isExpanded: boolean }>(
  ({ isExpanded }) => ({
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    svg: {
      transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
      color: 'white',
    },
  }),
);

const TabsSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    gap: '5px',
  },
}));

const TabItemContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded' && prop !== 'isActive' && prop !== 'hideTextOnMobile',
})<{ isExpanded: boolean; isActive: boolean; hideTextOnMobile?: boolean }>(
  ({ isExpanded, isActive, hideTextOnMobile, theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: isExpanded ? '8px' : '6px',
    gap: '6px',
    width: isExpanded ? '100%' : '42px',
    borderRadius: '30px',
    backgroundColor: isActive ? theme.palette.neutral.black : 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    border: `1px solid ${theme.palette.green[80]}`,

    '&:hover': {
      backgroundColor: theme.palette.green[80],
    },

    [theme.breakpoints.down('md')]: {
      background: isActive ? theme.palette.gradient.buttonGradient : 'transparent',
      border: `1px solid ${theme.palette.neutral[30]}`,

      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    [theme.breakpoints.down('sm')]: {
      width: hideTextOnMobile ? 'fit-content' : '100%',
    },
  }),
);

const TabIcon = styled('div', { shouldForwardProp: (prop) => prop !== 'isActive' })<{ isActive: boolean }>(
  ({ isActive, theme }) => ({
    color: isActive ? theme.palette.neutral.white : theme.palette.neutral.black,
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      color: theme.palette.neutral.black,
      width: '20px',
      height: '20px',
    },
  }),
);

const TabText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'hideTextOnMobile',
})<{ isActive: boolean; hideTextOnMobile?: boolean }>(({ isActive, hideTextOnMobile, theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  color: isActive ? theme.palette.neutral.white : theme.palette.neutral.black,
  whiteSpace: 'nowrap',
  fontSize: '14px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    color: theme.palette.neutral.black,
  },
  [theme.breakpoints.down('sm')]: {
    display: hideTextOnMobile || isActive ? 'none' : 'block',
  },
}));

export default SideBar;
export { SideBar };
